import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, filter, map, merge, switchMap } from 'rxjs';
import { CloudApiService } from './cloud-api.service';
import { CloudMetricsService } from '@/cloud/insights/cloud-metrics.service';
import { IPowerService } from 'src/api/power.service.interface';
import { OutletAction, OutletActionResult, PowerDevice, PowerFeature, PowerHistorySample, PowerOutlet, PowerReading, PowerUpdate } from 'src/api/power';

/** Linked device fields the power service reads (from any of the linked-device endpoints). */
interface LinkedDeviceRecord {
    id: string;
    name?: string;
    manufacturerName?: string | null;
    modelName?: string | null;
    online?: boolean;
    parentDeviceId?: string | null;
    locationName?: string | null;
    runtimeDeviceId?: number | string | null;
    capabilities?: { hasOutlets?: boolean } | null;
    state?: { features?: PowerFeature[]; webUiUrl?: string | null; outlets?: PowerOutlet[]; total?: PowerReading | null } | null;
}

interface StateUpdateEvent {
    parentDeviceId: string;
    /** Runtime device id on the processor, as raw JSON text. */
    linkedDeviceId: string | number;
    state?: { outlets?: Partial<PowerOutlet>[]; total?: PowerReading | null };
}

export type CloudPowerScope = { kind: 'device'; deviceId: string } | { kind: 'location'; namespaceId: string; locationId: string } | { kind: 'linkedDevice'; linkedDeviceId: string };

const HOUR_MS = 3600_000;

/**
 * Cloud mode power service. PDUs are linked devices with `capabilities.hasOutlets`: outlet
 * configuration comes from the check-in, control goes through the linked-device `setOutlet` action
 * (device tunnel), live readings arrive as `linkedDevice:{parent}:stateUpdate` and history comes
 * from the generic metrics API.
 *
 * Provide per component and call `setScope()` before use.
 */
@Injectable()
export class CloudPowerService implements IPowerService {
    private cloudApi = inject(CloudApiService);
    private metrics = inject(CloudMetricsService);

    readonly maxHistoryHours = 24 * 30;
    private readonly _canControl = signal(true);
    readonly canControl = this._canControl.asReadonly();

    private scope: CloudPowerScope | null = null;
    /** `${parentDeviceId}:${runtimeDeviceId}` -> linked device id */
    private runtimeKeys = new Map<string, string>();
    private readonly parents$ = new BehaviorSubject<string[]>([]);
    private readonly seeded$ = new BehaviorSubject<PowerUpdate | null>(null);

    readonly updates$: Observable<PowerUpdate> = merge(
        this.seeded$.pipe(filter((u): u is PowerUpdate => !!u)),
        this.parents$.pipe(
            switchMap((parents) => (parents.length ? merge(...parents.map((p) => this.cloudApi.subscribeToLinkedDeviceStateUpdates(p))) : EMPTY)),
            map((event: StateUpdateEvent | null) => this.toUpdate(event)),
            filter((u): u is PowerUpdate => !!u)
        )
    );

    setScope(scope: CloudPowerScope): void {
        this.scope = scope;
    }

    /** Controls are read-only while the processor's tunnel is down. */
    setTunnelConnected(connected: boolean): void {
        this._canControl.set(connected);
    }

    async getDevices(): Promise<PowerDevice[]> {
        const records = await this.loadRecords();
        const pdus = records.filter((r) => r.capabilities?.hasOutlets);

        this.runtimeKeys.clear();
        for (const r of pdus) {
            const parent = this.parentOf(r);
            if (parent && r.runtimeDeviceId != null) this.runtimeKeys.set(`${parent}:${r.runtimeDeviceId}`, r.id);
        }
        const parents = [...new Set(pdus.map((r) => this.parentOf(r)).filter((p): p is string => !!p))];
        if (parents.join() !== this.parents$.value.join()) this.parents$.next(parents);

        const devices = pdus.map((r) => this.toDevice(r));
        // Check-ins carry outlet state but not readings; fetch live values in the background.
        for (const device of devices) void this.seedLiveState(device);
        return devices;
    }

    async executeOutletAction(device: PowerDevice, outletId: number, action: OutletAction): Promise<OutletActionResult> {
        const result = await this.cloudApi.executeLinkedDeviceAction(device.key, 'setOutlet', { outletId, outletAction: action });
        return {
            success: !!result?.success,
            message: result?.message,
            outlet: result?.state?.outlets?.[0] ?? null
        };
    }

    async getHistory(device: PowerDevice, hours: number): Promise<PowerHistorySample[]> {
        const to = new Date();
        const result = await this.metrics.getLinkedDeviceMetrics(device.key, { type: 'power', from: new Date(to.getTime() - hours * HOUR_MS), to });
        const source = result.sources[0];
        if (!source) return [];

        const breakdown = source.breakdown ?? [];
        return source.points.map((p) => ({
            timestampUtc: p.t,
            periodSeconds: result.bucketMinutes * 60,
            totalAvgWatts: p.values['avgWatts'] ?? null,
            totalPeakWatts: p.values['peakWatts'] ?? null,
            totalEnergyKWh: p.values['energyKWh'] ?? null,
            outlets: breakdown.flatMap((b) => {
                const point = b.points.find((bp) => bp.t === p.t);
                return point ? [{ outletId: Number(b.key), name: b.name ?? `Outlet ${b.key}`, avgWatts: point.values['avgWatts'] ?? 0, peakWatts: point.values['peakWatts'] ?? 0, energyKWh: point.values['energyKWh'] ?? null }] : [];
            })
        }));
    }

    private async loadRecords(): Promise<LinkedDeviceRecord[]> {
        const scope = this.scope;
        if (!scope) return [];
        switch (scope.kind) {
            case 'device':
                return ((await this.cloudApi.getLinkedDevices(scope.deviceId)) ?? []).map((r: LinkedDeviceRecord) => ({ ...r, parentDeviceId: scope.deviceId }));
            case 'location':
                return (await this.cloudApi.getAllLinkedDevices(scope.namespaceId, scope.locationId)) ?? [];
            case 'linkedDevice': {
                const record = await this.cloudApi.getLinkedDevice(scope.linkedDeviceId);
                return record ? [record] : [];
            }
        }
    }

    private async seedLiveState(device: PowerDevice): Promise<void> {
        try {
            const response = await this.cloudApi.executeLinkedDeviceAction(device.key, 'getState');
            const state = response?.state;
            if (!state) return;
            this.seeded$.next({ key: device.key, outlets: state.outlets ?? [], total: state.total ?? null, online: state.online ?? device.online });
        } catch {
            // Tunnel down: outlet state from the last check-in still shows, controls go read-only.
            this._canControl.set(false);
        }
    }

    private toDevice(r: LinkedDeviceRecord): PowerDevice {
        const state = r.state ?? {};
        return {
            key: r.id,
            id: Number(r.runtimeDeviceId ?? 0),
            name: r.name ?? 'PDU',
            manufacturerName: r.manufacturerName,
            modelName: r.modelName,
            online: !!r.online,
            features: state.features ?? ['Switched'],
            webUiUrl: state.webUiUrl ?? null,
            roomName: r.locationName ?? null,
            total: state.total ?? null,
            outlets: (state.outlets ?? []).map((o) => ({ ...o, reading: o.reading ?? null }))
        };
    }

    private parentOf(r: LinkedDeviceRecord): string | null {
        return r.parentDeviceId ?? (this.scope?.kind === 'device' ? this.scope.deviceId : null);
    }

    private toUpdate(event: StateUpdateEvent | null): PowerUpdate | null {
        if (!event?.state) return null;
        const key = this.runtimeKeys.get(`${event.parentDeviceId}:${String(event.linkedDeviceId).replace(/"/g, '')}`);
        if (!key) return null;
        return { key, outlets: event.state.outlets, total: event.state.total };
    }
}
